import React, { useState } from 'react';
import { Link } from '@reach/router';
import Tab from '../../../components/Tab';
import Tabs from '../../../components/Tabs';
import ReadingBookItem from './ReadingBookItem';
import styles from '../Dashboard.module.scss';
import cx from 'classnames';
import { ArrowRightCircle, PlusCircle, Target } from 'react-feather';
import { BOOK_STATUS } from '../../../services/constants';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import Reviews from './Reviews';
import { ProgressBar } from '@squirrellearningltd/go-pupil-components';
import homeBook from '../../../images/home-book.png';
import gem from '../../../images/gem.svg';
import badge from '../../../images/badge.png';
import loader from '../../../images/loader-boomer.png';
import openBook from '../../../images/open_book.png';
import reviewBook from '../../../images/review_book.png';
import diamondGems from '../../../images/diamond-gems.png';
import streakBook from '../../../images/streak-book.png';

const DAILY_GEM_TARGET = 3;

const getProgressPercentage = (progress = 0, target = 0) => {
    if (!target) return 0;

    return Math.min(100, Math.max(0, Math.round((progress / target) * 100)));
}

const getCount = value => Number(value) || 0;

const ReadingBooks = ({
    student = {},
    readingBooks = [],
    targets = {}
}) => {

    const [showCurrent, setShowCurrent] = useState(true);
    const [currentBooksToShow, setCurrentBooksToShow] = useState(4);
    const [completedBooksToShow, setCompletedBooksToShow] = useState(5);
    const [reviewsLoadCount, setReviewsLoadCount] = useState(0);
    const { t } = useTranslation();
    const {
        firstName,
        name: studentName,
        boomerAvatarUrl,
        backgroundImageUrl,
        availableGems,
        dailyTotalGemsEarned,
        academicYearTotalBooksCompleted,
        academicYearTotalGemsEarned,
        dailyStreak,
        currentReadingRank
    } = student;
    const {
        student: studentTargets = {},
        parent: parentTargets = {},
        loading: targetsLoading = false
    } = targets;
    const readingBand = currentReadingRank ? currentReadingRank.readingBandRank : null;

    const orderedBooks = [
        ..._.orderBy(readingBooks.filter(book => book.bookType === 1), 'book.title', 'asc'),
        ..._.orderBy(readingBooks.filter(book => book.bookType !== 1), 'book.title', 'asc')
    ];

    const currentBooks = orderedBooks.filter(book => book.status !== BOOK_STATUS.completed);
    const completedBooks = orderedBooks.filter(book => book.status === BOOK_STATUS.completed);
    const visibleBooks = showCurrent ? currentBooks.slice(0, currentBooksToShow) : completedBooks.slice(0, completedBooksToShow);
    const hasMoreBooks = showCurrent ? currentBooks.length > currentBooksToShow : completedBooks.length > completedBooksToShow;
    const dailyGems = getCount(dailyTotalGemsEarned);
    const gemBalance = getCount(availableGems);
    const booksCompleted = getCount(academicYearTotalBooksCompleted);
    const gemsEarned = getCount(academicYearTotalGemsEarned);
    const streak = getCount(dailyStreak);
    const studentTarget = getCount(studentTargets.target);
    const studentProgress = getCount(studentTargets.progress);
    const homeTarget = getCount(parentTargets.target);
    const homeProgress = getCount(parentTargets.progress);
    const dailyGemProgress = getProgressPercentage(dailyGems, DAILY_GEM_TARGET);
    const studentLogsProgress = getProgressPercentage(studentProgress, studentTarget);
    const homeLogsProgress = getProgressPercentage(homeProgress, homeTarget);

    const loadMoreBooks = () => {
        if (showCurrent) {
            setCurrentBooksToShow(currentBooksToShow + 5);
        } else {
            setCompletedBooksToShow(completedBooksToShow + 5);
        }

        setReviewsLoadCount(reviewsLoadCount + 1);
    }

    const showCurrentBooks = () => {
        setShowCurrent(true);
        setCurrentBooksToShow(4);
    }

    const showCompletedBooks = () => {
        setShowCurrent(false);
        setCompletedBooksToShow(5);
    }
    
    return (
        <div className={styles.dashboard}>
            <div className={styles.dashboardTop}>
                <div className={styles.studentPanel}>
                    <div className={styles.studentArtwork} style={backgroundImageUrl ? { backgroundImage: `url(${JSON.stringify(backgroundImageUrl)})` } : undefined}>
                        <img src={boomerAvatarUrl || loader} alt="" />
                    </div>
                    <div className={styles.studentDetails}>
                        <h1>{studentName || firstName}</h1>
                        <div className={styles.gemBalance}>
                            <img src={gem} alt="" />
                            <span>{gemBalance} {gemBalance === 1 ? 'gem' : 'gems'}</span>
                        </div>
                        <p className={styles.gemProgressTitle}>
                            {dailyGems}/{DAILY_GEM_TARGET} Gem chests collected today
                        </p>
                        <ProgressBar
                            progress={dailyGemProgress}
                            backgroundColor="#CF3636"
                            showProgressbadge
                            progressBadge={gem}
                        />
                    </div>
                </div>
                <div className={styles.targetsPanel}>
                    <div className={styles.panelHeading}>
                        <Target size={40} color="#CF3636" />
                        <h2>Your Targets</h2>
                    </div>
                    <div className={styles.targetRow}>
                        <img
                            className={styles.targetIconImage}
                            src={badge}
                            alt=""
                            title={`You have ${studentProgress ?? 0} / ${studentTarget ?? 0} this week`}

                        />
                        <div className={styles.readingTargetStats}> {studentProgress?? 0} / {studentTarget ?? 0}</div>
                        <div
                            className={styles.targetContent}
                            aria-label={targetsLoading ? 'Boomer logs loading' : `Boomer logs ${studentProgress} of ${studentTarget}`}
                            title={`You have ${studentProgress ?? 0} / ${studentTarget ?? 0} this week`}
                        >
                            <ProgressBar progress={studentLogsProgress} backgroundColor="#1B8DFD" />
                        </div>
                    </div>
                    <div className={styles.targetRow}>
                        <img
                            className={styles.targetIconImage}
                            src={homeBook}
                            alt=""
                            title={`You have ${homeProgress ?? 0} / ${homeTarget ?? 0} this week`}
                        />
                        <div className={styles.readingTargetStats}> {homeProgress ?? 0} / {homeTarget ?? 0}</div>
                        <div
                            className={styles.targetContent}
                            aria-label={targetsLoading ? 'Home logs loading' : `Home logs ${homeProgress} of ${homeTarget}`}
                            title={`You have ${homeProgress ?? 0} / ${homeTarget ?? 0} this week`}
                        >
                            <ProgressBar progress={homeLogsProgress} backgroundColor="#1B8DFD" />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.dashboardContent}>
                <div className={cx('content-panel', styles.booksPanel)}>
                    {readingBand && (
                        <h1 className={styles.readingBand}>{t('reading_band')}: <span className={styles.readingBandMeta}><span className={styles.readingBandColor} style={{ backgroundColor: readingBand.colourCode }}></span>{readingBand.name}</span></h1>
                    )}
                    <Tabs>
                        <Tab active={showCurrent} onClick={showCurrentBooks} >{t('current_books')}</Tab>
                        <Tab active={!showCurrent} onClick={showCompletedBooks}>{t('completed_books')}</Tab>
                    </Tabs>
                    <div className={styles.bookList}>
                        {showCurrent && (
                            <Link className={styles.bookListItem} to='/book/new'>
                                <div className={cx('card', 'card-hover', styles.addBookCard)}>
                                    <h2>{t('add_new_book')}</h2>
                                    <span className={styles.addBookIcon}><PlusCircle size="50" /></span>
                                    <p>Find and add a new book to read</p>
                                </div>
                            </Link>
                        )}

                        {visibleBooks.map(book => (
                            <ReadingBookItem key={`book-${book.id}`} book={book.book} readingBook={book} />
                        ))}

                        {!showCurrent && !completedBooks.length && <p>{t('no_books_completed_yet')}</p>}
                    </div>

                    {hasMoreBooks && (
                        <div className={styles.loadMore}>
                            <button onClick={loadMoreBooks} className={styles.loadMoreButton}>
                                {t('load_more')}
                                <ArrowRightCircle size="24" />
                            </button>
                        </div>
                    )}
                </div>
                <div className={styles.dashboardSide}>
                    <div className={styles.progressPanel}>
                        <div className={styles.panelHeading}>
                            <img className={styles.panelHeadingImage} src={reviewBook} alt="" />
                            <h2>Your Progress</h2>
                        </div>
                        <div className={styles.progressItems}>
                            <div className={styles.progressItem}>
                                <div className={styles.progressValue}>
                                    <img src={openBook} alt="" />
                                    <strong>{booksCompleted}</strong>
                                </div>
                                <span>Books Read</span>
                            </div>
                            <div className={styles.progressItem}>
                                <div className={styles.progressValue}>
                                    <img src={diamondGems} alt="" />
                                    <strong>{gemsEarned}</strong>
                                </div>
                                <span>Gems Earned</span>
                            </div>
                            <div className={styles.progressItem}>
                                <div className={styles.progressValue}>
                                    <img src={streakBook} alt="" />
                                    <strong>{streak}</strong>
                                </div>
                                <span>Day Streak</span>
                            </div>
                        </div>
                    </div>
                    <div className={styles.bookReviewsPanel}>
                        <Reviews loadMoreCount={reviewsLoadCount} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ReadingBooks;
